import Message, { IMessage } from "./Message";
import { getKeys } from "../utils/helpers";
import { getServerDate } from "../utils/storage/date";
import constants from "../constants";
import { DriveStore } from "../store/reducers";
import { extend } from "../utils/vanilla";
import { getMessagesDictionaryFromStore } from "../ui/messaging/helpers";

interface IGetMessageOptions {
    sortAscending?: boolean;
    activeOnly?: boolean;
    isDirectionToVehicle?: boolean;
    includeGoTalk?: boolean;
    groupRouteMessages?: boolean;
    filterUserId?: string;
}

export default class Messages {
    private messages: IIdDictionary<IMessage> = {};
    private readonly store: DriveStore;
    private readonly currentUserId: string;

    constructor(store: DriveStore, currentUserId?: string) {
        this.store = store;
        this.currentUserId = currentUserId || "";
    }

    get keys(): string[] {
        return getKeys(this.messages);
    }

    get length(): number {
        return this.keys.length;
    }

    /**
     * Sorting comparator by sent date
     * @param isChronological Whether or not to sort in chronological order
     */
    static messageComparator(isChronological = true) {
        return (a: IMessage, b: IMessage) => {
            if (!a.sent || !b.sent) {
                return 0;
            }
            let sentComparison = a.sent > b.sent ? 1 : a.sent < b.sent ? -1 : 0;
            return isChronological ? sentComparison : -1 * sentComparison;
        };
    }

    fetch() {
        this.messages = getMessagesDictionaryFromStore(this.store, this.currentUserId);
    }

    /**
     * Gets messages from storage based on filtered options
     * @param options The options to filter the data
     * @param overrideUserId Overrides the user store we're getting the messages from
     */
    getMessages(options?: IGetMessageOptions): Message[] {
        let opts = {...{
                activeOnly: true,
                includeGoTalk: false,
                groupRouteMessages: false
            } as IGetMessageOptions, ...options};
        let messagesDict: DeepReadonlyObject<IIdDictionary<IMessage>> = this.store.getState().messages[this.currentUserId] || {};
        let currentTimestamp = getServerDate().toISOString();
        let messages: Message[] = Object.keys(messagesDict).reduce((messagesList, messageId) => {
            const message = extend(true, {}, messagesDict[messageId]) as IMessage;

            let isUserIdValid = (msg: IMessage) => {
                if (msg.user && msg.user.id !== "NoUserId") {
                    return opts.filterUserId !== msg.user.id;
                }
                return opts.filterUserId !== "NoUserId";
            };

            // Filter out messages that don't meet the params
            if ((!opts.includeGoTalk && message.messageContent?.contentType === "GoTalk") ||
                (typeof (opts.isDirectionToVehicle) !== "undefined" && message.isDirectionToVehicle !== opts.isDirectionToVehicle) ||
                (opts.activeOnly && message.activeTo && message.activeTo < currentTimestamp) ||
                (typeof (opts.filterUserId) !== "undefined" && (!message.user || isUserIdValid(message)))) {
                return messagesList;
            }

            // Otherwise, add this message to the output
            messagesList.push(new Message(message));
            return messagesList;
        }, [] as Message[]);
        if (opts.groupRouteMessages) {
            messages = this.bundleRouteMessages(messages);

            // Bundling of route messages automatically sorts in ascending order, so no need to re-sort this
            // (just use reverse() if the result is needed in descending order)
            if (typeof (opts.sortAscending) !== "undefined") {
                return opts.sortAscending ? messages.reverse() : messages;
            }
        }
        if (typeof (opts.sortAscending) !== "undefined") {
            return messages.sort(Messages.messageComparator(opts.sortAscending));
        }
        return messages;
    }

    /**
     * Find related grouped route messages in storage for a passed-in message
     * @param firstMessageInGroup The first (chronological) message in a series of route messages
     * @param currentUserId The current user id we're getting messages for from the store
     */
    getRelatedRouteMessages(firstMessageInGroup: Message): Message[] {
        let messages = this.getMessages(undefined);
        return messages.sort(Messages.messageComparator(true)).filter(message => {
            let messageContent = message.messageContent;
            if (messageContent && messageContent.routeId && firstMessageInGroup.messageContent?.routeId === messageContent.routeId) {
                return this.isMessageInGroupTimeThreshold(firstMessageInGroup, message);
            }
            return false;
        });
    }

    /**
     * Finds whether or not a message, messageToCheck, can be grouped with the given parent message on a basis of the timestamp
     * @param parentMessage The reference message, the first chronologically in a route grouping
     * @param messageToCheck The message that needs to be checked to see if it fits in the grouping
     */
    isMessageInGroupTimeThreshold(parentMessage: Message, messageToCheck: Message): boolean {
        let gracePeriodMs = constants.MESSAGING.ROUTE_GROUPING_GRACE_INTERVAL_SECS * 1000;
        let messageSentTime = messageToCheck.getSent();
        let parentMessageTime = parentMessage && parentMessage.getSent();
        return !!(
            parentMessageTime &&
            messageSentTime &&
            messageSentTime.getTime() >= parentMessageTime.getTime() &&
            messageSentTime < new Date(parentMessageTime.getTime() + gracePeriodMs)
        );
    }

    /**
     * Returns a chronologically sorted list of grouped messages
     * @param messages The list of Message items
     * @param firstMessageInGroup The message to find related route items for
     */
    bundleRouteMessages(messages: Message[]): Message[] {
        let lastGroupedMessage: Message | undefined;
        return messages.sort(Messages.messageComparator(true)).reduce((returnedMessages, message) => {
            let messageContent = message.messageContent;
            if (messageContent && messageContent.routeId) {
                if (!lastGroupedMessage
                    || lastGroupedMessage.messageContent?.routeId !== messageContent.routeId
                    || !this.isMessageInGroupTimeThreshold(lastGroupedMessage, message)) {
                    lastGroupedMessage = message;
                    returnedMessages.unshift(message);
                } else {
                    let groupedMessages = lastGroupedMessage._groupedMessages;
                    if (!groupedMessages) {
                        groupedMessages = [];
                        let clonedGroupMessage = { ...lastGroupedMessage };
                        groupedMessages.push(clonedGroupMessage);
                    }
                    groupedMessages.push(message);
                    lastGroupedMessage._groupedMessages = groupedMessages;
                }
                return returnedMessages;
            } else {
                returnedMessages.unshift(message);
                return returnedMessages;
            }
        }, [] as Message[]);
    }
}